import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { Auth } from '../services/auth';

export const AUTH_RETRY = new HttpContextToken<boolean>(() => false);

const AUTH_ANON_PATHS = ['/auth/login', '/auth/refresh-token', '/auth/logout'];

function isAnonymousAuthRequest(url: string): boolean {
  return AUTH_ANON_PATHS.some((path) => url.includes(path));
}

function isInvalidRefreshStatus(status: number): boolean {
  return status === 401 || status === 400;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(Auth);
  const router = inject(Router);

  const skipAuth = isAnonymousAuthRequest(req.url);
  const token = skipAuth ? null : authService.getAccessToken();
  const outgoing = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(outgoing).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (skipAuth || req.context.get(AUTH_RETRY)) {
        return throwError(() => error);
      }

      if (!authService.hasValidRefreshToken()) {
        authService.clearSession();
        router.navigate(['/']);
        return throwError(() => error);
      }

      return authService.refreshAccessToken().pipe(
        catchError((refreshError: unknown) => {
          if (
            refreshError instanceof HttpErrorResponse &&
            isInvalidRefreshStatus(refreshError.status)
          ) {
            authService.clearSession();
            router.navigate(['/']);
          }
          return throwError(() => refreshError);
        }),
        switchMap(() => {
          const newToken = authService.getAccessToken();
          const retryReq = req.clone({
            context: req.context.set(AUTH_RETRY, true),
            ...(newToken ? { setHeaders: { Authorization: `Bearer ${newToken}` } } : {})
          });
          return next(retryReq);
        })
      );
    })
  );
};
